import { FocusMonitor, HighContrastMode, HighContrastModeDetector, LiveAnnouncer } from '@angular/cdk/a11y';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  output,
  signal,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import { HxBaseControl } from '../../base/hx-base-control';
import { HxIcon } from '../icon/icon';

/** Semantic variant for alert context. Determines color scheme and default icon. */
export type AlertVariant = 'info' | 'success' | 'warning' | 'danger' | 'neutral' | 'none';

/** Visual appearance style for alert. Determines background treatment and visual hierarchy. */
export type AlertAppearance = 'subtle' | 'outlined' | 'solid';

/** Default icon names for each semantic variant. */
const ALERT_DEFAULT_ICONS: Record<AlertVariant, string> = {
  info: 'info-circle',
  success: 'check-circle',
  warning: 'exclamation-triangle',
  danger: 'x-circle',
  neutral: 'info-circle',
  none: '', // No default icon for none variant
};

/**
 * HxAlert Component
 *
 * Displays important, time-sensitive notifications aligned to Moon Design System specifications. Supports 4 semantic
 * variants (info, success, warning, danger) and 3 appearance styles (subtle, outlined, solid) for comprehensive visual
 * hierarchy.
 *
 * @example
 *   ```html
 *   <!-- Basic usage -->
 *   <hx-alert
 *     variant="info"
 *     description="Your changes have been saved."
 *   />
 *
 *   <!-- With title and dismissible -->
 *   <hx-alert
 *     variant="success"
 *     title="Success!"
 *     description="Profile updated successfully."
 *     [dismissible]="true"
 *     (onDismiss)="handleDismiss()"
 *   />
 *
 *   <!-- With action buttons -->
 *   <hx-alert variant="warning" description="Action required">
 *     <button hxButton>Take Action</button>
 *   </hx-alert>
 *   ```;
 *
 * @accessibility
 * - WCAG 2.1 Level AA compliant
 * - role="alert" with aria-live="polite" for screen reader announcements
 * - Keyboard navigation: Tab, Enter, Space, Escape
 * - 44×44px minimum touch targets for interactive elements
 * - High contrast mode support
 */
@Component({
  selector: 'hx-alert',
  standalone: true,
  imports: [HxIcon],
  templateUrl: './alert.html',
  styleUrl: './alert.css',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class]': 'hostClasses()',
    '[class.hx-alert--dismissed]': 'dismissed()',
    '[class.hx-alert--high-contrast]': 'isHighContrast()',
  },
})
export class HxAlert extends HxBaseControl implements AfterViewInit {
  /**
   * Semantic variant determining color scheme and default icon.
   *
   * @default 'info'
   */
  readonly variant = input<AlertVariant>('info');

  /**
   * Visual appearance style.
   *
   * @default 'subtle'
   */
  readonly appearance = input<AlertAppearance>('subtle');

  /**
   * Whether alert can be dismissed with close button.
   *
   * @default false
   */
  readonly dismissible = input<boolean>(false);

  /** Optional alert title (bold text above description). */
  readonly title = input<string>();

  /** Alert message content. */
  readonly description = input<string>();

  /** Custom icon name to override default variant icon. Set to null to hide icon entirely. */
  readonly customIcon = input<string | null>();

  /** Width of the alert container (CSS value: %, px, rem, etc.). */
  readonly width = input<string>('100%');

  /** Text alignment for alert content. */
  readonly contentJustification = input<'left' | 'center' | 'right'>('left');

  /** Emitted when alert is dismissed via close button. Parent responsible for removing alert from DOM. */
  readonly onDismiss = output<void>();

  /** Internal dismissed state for exit animation. */
  protected readonly dismissed = signal<boolean>(false);

  /** Close button element reference for focus monitoring. */
  private readonly closeButtonRef = viewChild<ElementRef<HTMLButtonElement>>('closeButton');

  /** CDK services for accessibility. */
  private focusMonitor = inject(FocusMonitor);
  private liveAnnouncer = inject(LiveAnnouncer);
  private highContrastDetector = inject(HighContrastModeDetector);

  /** Computed icon name based on variant and customIcon input. */
  protected readonly iconName = computed<string | null>(() => {
    const custom = this.customIcon();
    if (custom !== undefined) {
      return custom; // null explicitly removes icon, string uses custom icon
    }
    return ALERT_DEFAULT_ICONS[this.variant()];
  });

  /** Computed CSS classes for styling. */
  protected readonly hostClasses = computed(() => {
    return `hx-alert hx-alert--${this.variant()} hx-alert--${this.appearance()}`;
  });

  /** Detect Windows High Contrast Mode. */
  protected readonly isHighContrast = computed(() => {
    return this.highContrastDetector.getHighContrastMode() !== HighContrastMode.NONE;
  });

  ngAfterViewInit(): void {
    // Monitor focus on close button for keyboard-only focus indicators
    const closeButton = this.closeButtonRef();
    if (closeButton) {
      this.focusMonitor.monitor(closeButton.nativeElement, true);
    }
  }

  /** Handle alert dismissal. Sets dismissed state, announces to screen readers, and emits event. */
  protected handleDismiss(): void {
    this.dismissed.set(true);
    void this.liveAnnouncer.announce('Alert dismissed', 'polite');

    // Emit after animation completes (150ms exit animation)
    setTimeout(() => {
      this.onDismiss.emit();
    }, 150);
  }

  /** Handle Escape key for dismissal. */
  protected handleKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && this.dismissible()) {
      event.preventDefault();
      this.handleDismiss();
    }
  }
}
